import React, { useMemo } from 'react';
import {
  OverlayTrigger,
  Stack,
  Tooltip,
} from 'react-bootstrap';
import { BsChevronDown, BsChevronUp } from 'react-icons/bs';
import PropTypes from 'prop-types';

import Loader from 'components/elements/Loader';
import Pagination from 'components/elements/Pagination';
import { FixedTable, FlexCenter } from 'resources/styles/display';

const TruncatedCellContent: React.FC<{
  children: React.ReactNode;
  className?: string;
}> = React.memo(({ children, className = 'cell-content' }) => {
  const contentRef = React.useRef<HTMLSpanElement | null>(null);
  const tooltipId = React.useId();
  const [isTruncated, setIsTruncated] = React.useState(false);
  const [tooltipText, setTooltipText] = React.useState('');
  const isHeaderLabel = className === 'label';

  const updateTooltip = React.useCallback(() => {
    const element = contentRef.current;
    if (!element) return;

    const truncated = element.scrollWidth > element.clientWidth
      || element.scrollHeight > element.clientHeight;
    setIsTruncated(truncated);

    const fullText = element.textContent?.trim();
    setTooltipText(fullText || '');
  }, []);

  React.useEffect(() => {
    updateTooltip();
  }, [children, updateTooltip]);

  React.useEffect(() => {
    const element = contentRef.current;
    if (!element || typeof ResizeObserver === 'undefined') return undefined;

    const observer = new ResizeObserver(() => {
      updateTooltip();
    });

    observer.observe(element);
    return () => {
      observer.disconnect();
    };
  }, [updateTooltip]);

  const content = (
    <span
      ref={contentRef}
      className={className}
      onMouseEnter={updateTooltip}
    >
      {children}
    </span>
  );

  if (!isTruncated || !tooltipText) return content;

  return (
    <OverlayTrigger
      placement="top"
      trigger={['hover', 'focus']}
      delay={isHeaderLabel ? { show: 120, hide: 80 } : undefined}
      overlay={<Tooltip id={tooltipId}>{tooltipText}</Tooltip>}
    >
      <span className="d-block w-100">{content}</span>
    </OverlayTrigger>
  );
});

TruncatedCellContent.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
};

export interface Column<T> {
  id: keyof T | string;
  label: string;
  nonSortable?: boolean;
  headerStyle?: React.CSSProperties;
  columnStyle?: React.CSSProperties;
  override?: (item: T) => React.ReactNode;
}

export interface PaginationProps {
  page: number;
  total: number;
  limit: number;
}

export interface TableProps<T> {
  columns: Column<T>[];
  items: T[];
  order?: string;
  orderDirection?: 'asc' | 'desc';
  pagination?: PaginationProps;
  handlePageChange: (page: number) => void;
  handleOrderChange?: (order: string) => void;
  onClickRow?: (item: T) => void;
  isLoading?: boolean;
  minTableWidth?: string | number;
}

const Table = <T extends { [key: string]: any }>({
  columns,
  items,
  order,
  orderDirection,
  pagination,
  handlePageChange,
  handleOrderChange,
  onClickRow,
  isLoading = false,
  minTableWidth,
}: TableProps<T>) => {
  const getCellValue = React.useCallback((item: T, id: keyof T | string) => {
    if (typeof id !== 'string') return item[id as keyof T];
    if (!id.includes('.')) return item[id];

    return id.split('.').reduce<any>((acc, key) => {
      if (acc == null) return undefined;
      return acc[key];
    }, item);
  }, []);

  const colWidths = useMemo(() => {
    const fallbackPercent = 100 / Math.max(columns.length, 1);
    const rawWidths = columns.map((c) => {
      const width = c.headerStyle?.width ?? c.columnStyle?.width;
      return width == null ? `${fallbackPercent}%` : String(width);
    });

    const parsedPercentWidths = rawWidths.map((width) => {
      const match = width.trim().match(/^([0-9]*\.?[0-9]+)%$/);
      return match ? Number(match[1]) : null;
    });

    const allPercent = parsedPercentWidths.every((value) => value !== null);
    if (!allPercent) {
      return rawWidths.map((width) => ({ width }));
    }

    const totalPercent = parsedPercentWidths.reduce((sum, value) => sum + (value ?? 0), 0);
    const ratio = totalPercent > 100 ? 100 / totalPercent : 1;

    return parsedPercentWidths.map((value) => ({
      width: `${(value ?? fallbackPercent) * ratio}%`,
    }));
  }, [columns]);

  const resolvedMinTableWidth = useMemo(() => {
    if (minTableWidth !== undefined) return minTableWidth;
    return `${Math.max(680, columns.length * 110)}px`;
  }, [minTableWidth, columns.length]);

  return (
    <div
      style={{
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        position: 'relative',
        minHeight: '200px',
      }}
    >
      {isLoading && (
        <FlexCenter
          style={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            background: 'rgba(0, 0, 0, 0.4)',
            inset: 0,
          }}
        >
          <Loader />
        </FlexCenter>
      )}

      <div
        style={{
          width: '100%',
          maxWidth: '100%',
          minWidth: 0,
          overflowX: 'auto',
          overflowY: 'hidden',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        <FixedTable
          striped
          hover={!!onClickRow}
          className="fixed-table"
          style={{ minWidth: resolvedMinTableWidth }}
        >
          <colgroup>
            {colWidths.map((style, i) => (
              <col key={String(columns[i].id ?? i)} style={style} />
            ))}
          </colgroup>

          <thead className="border-top">
            <tr>
              {columns.map(({
                id, label, nonSortable, headerStyle = {},
              }, i) => (
                <th
                  key={String(id ?? i)}
                  onClick={() => !nonSortable && handleOrderChange?.(String(id))}
                  style={{ ...headerStyle, cursor: nonSortable ? '' : 'pointer' }}
                >
                  <Stack
                    as="span"
                    direction="horizontal"
                    gap={2}
                    className="th-inner"
                  >
                    <TruncatedCellContent className="label">
                      {label}
                    </TruncatedCellContent>
                    <span className="sort-slot">
                      {(() => {
                        if (nonSortable || order !== id) return null;
                        if (orderDirection === 'asc') return <BsChevronDown />;
                        return <BsChevronUp />;
                      })()}
                    </span>
                  </Stack>
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {items?.map((item, rowIndex) => (
              <tr
                key={String(item.id ?? rowIndex)}
                onClick={() => onClickRow?.(item)}
                style={{ cursor: onClickRow ? 'pointer' : '' }}
              >
                {columns.map(({ id, override, columnStyle = {} }) => (
                  <td
                    style={columnStyle}
                    key={`${String(item.id ?? rowIndex)}:${String(id)}`}
                  >
                    <TruncatedCellContent>
                      {override ? override(item) : getCellValue(item, id)}
                    </TruncatedCellContent>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </FixedTable>
      </div>

      {pagination && (
        <Pagination
          total={pagination.total}
          limit={pagination.limit}
          page={pagination.page}
          onPageChange={handlePageChange}
        />
      )}
    </div>
  );
};

export default Table;
